import { type Requisition, type ToolFundSettings } from '@kalos/kalos-rpc';

/** A request's line (one fulfilment) as the Tool Log's item table shows it. */
export interface FulfillmentItemRow {
  id: number;
  toolName: string;
  categoryId: number;
  quantity: number;
  status: number;
  statusDesc: string;
  estimatedCost: number;
  actualCost?: number;
  notes?: string;
  assetTag?: string;
}

/**
 * A request's lines, from its fulfilments, each with the requested item's name, category,
 * quantity, estimate and description. Desktop's item table and the phone request page read this.
 */
export const transformFulfillmentItems = (requisition: Requisition): FulfillmentItemRow[] => {
  if (!requisition.fulfillments || requisition.fulfillments.length === 0) {
    return [];
  }

  return requisition.fulfillments.map((fulfillment) => {
    // Find the corresponding requisition item for additional data
    const requisitionItem = requisition.items?.find(
      (item) => item.id === fulfillment.requisitionItemId,
    );

    return {
      id: fulfillment.id,
      toolName: requisitionItem?.customItemName || 'Unknown Tool',
      categoryId: requisitionItem?.categoryId || 0,
      quantity: requisitionItem?.quantityRequested || 0,
      status: fulfillment.statusId || 0,
      statusDesc:
        ['Unknown', 'Pending', 'Approved', 'Rejected'][fulfillment.statusId || 0] || 'Unknown',
      estimatedCost: requisitionItem?.estimatedCost || 0,
      actualCost: fulfillment.actualCost || undefined,
      notes: requisitionItem?.customItemDescription || fulfillment.notes || undefined,
      assetTag: fulfillment.assetTag || undefined,
    };
  });
};

/**
 * Desktop's Total Cost: the actual cost of each line added up, where a rejected line (status 3)
 * counts as nothing. Each actual cost already includes the quantity (unit price × quantity).
 */
export const requisitionTotalActualCost = (requisition: Pick<Requisition, 'fulfillments'>) =>
  requisition.fulfillments?.reduce((sum, fulfillment) => {
    const actualCost = fulfillment.actualCost || 0;
    if (fulfillment.statusId === 3) {
      return sum;
    }
    // actualCost already includes quantity (unit price × quantity)
    return sum + actualCost;
  }, 0) || 0;

/**
 * The words on desktop's status badges. A request (`isRequisitionLevel`) is Incomplete, In
 * Progress or Complete; a line is Pending, Approved, Rejected or Completed.
 */
export const toolRequestStatusLabel = (statusId: number, isRequisitionLevel = false) => {
  switch (statusId) {
    case 1:
      return isRequisitionLevel ? 'Incomplete' : 'Pending';
    case 2:
      return isRequisitionLevel ? 'Complete' : 'Approved';
    case 3:
      return isRequisitionLevel ? 'In Progress' : 'Rejected';
    case 4:
      return isRequisitionLevel ? 'Complete' : 'Completed';
    default:
      return 'Unknown';
  }
};

/** What desktop's disabled request button says when someone can't request tools. */
export const toolFundIneligibleLabel = (
  settings: Pick<ToolFundSettings, 'isEligible'> | undefined,
) => (settings?.isEligible ? 'Insufficient Balance' : 'Not Eligible to Request Tools');

/** Charged to someone other than whoever created it: an admin recorded it for the employee. */
export const isEnteredByAdmin = (r: Pick<Requisition, 'createdBy' | 'chargedToEmpId'>) =>
  r.chargedToEmpId > 0 && r.createdBy !== r.chargedToEmpId;
